import { Box, Typography, Link } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useL10n, useIsMobile } from '@uplift/hooks';
import { EditableSectionContainer, EditableSectionRow, useEditableSectionsContext } from '../../../EditableSection';
import { Button } from '../../../Button';
import React, { useState } from 'react';
import { InsuranceFormBlock, PrimarySubscriberFormBlock, useInsuranceForm } from '../forms';
import { ContentStack } from './content-stack.component';
import { InsuranceSection } from '../Settings/types';
import { formatAddress } from '../Settings/helpers';
import { useSettingsStyles } from '../../../molecules';
import { ViewInsuranceModal } from './view-insurance-modal.component';
import { useInsurances } from '../hooks/use-insurance.hook';


export type InsuranceSectionItemProps = {
  insuranceSection: InsuranceSection;
  title: string;
  patientId: string;
  onDeleteInsurance: (insuranceSection: InsuranceSection) => void;
};

type InsuranceViewState = 'viewCard' | null;

export const InsuranceSectionItem: React.VFC<InsuranceSectionItemProps> = ({ insuranceSection, title, patientId, ...props }) => {
  const l10n = useL10n();
  const { classes: settingsClasses } = useSettingsStyles();
  const isMobile = useIsMobile('sm');
  const [ viewState, setViewState ] = useState<InsuranceViewState>(null);
  const { setEditKey } = useEditableSectionsContext();
  const { insuranceFormik } = useInsuranceForm(patientId, insuranceSection);
  const { data: insurancesData } = useInsurances(patientId);
  const insuranceData = insurancesData?.[insuranceSection];

  const onDeleteInsurance = () => {
    setEditKey(null);
    props.onDeleteInsurance(insuranceSection);
  };

  const primarySubscriberName = React.useMemo(() => {
    return `${insuranceData?.firstName} ${insuranceData?.lastName}`;
  }, [ insuranceData ]);

  const primarySubscriberAddressFormatted = insuranceData
    ? formatAddress(insuranceData, insuranceData.stateProfile)
    : null;

  return (
    <>
      {viewState === 'viewCard' && (
        <ViewInsuranceModal
          onClose={() => setViewState(null)}
          {...insuranceData}
        />
      )}

      <EditableSectionContainer
        title={title}
        sectionEditKey={insuranceSection}
        formikConfigs={{
          [insuranceSection]: insuranceFormik
        }}
        disableFixedEditAction={!isMobile}
        headerStickyOrder={1}
        headerClassName={isMobile ? settingsClasses.insuranceHeader : undefined}
      >
        <EditableSectionRow
          label="Network"
          rowKey="network"
          form={<InsuranceFormBlock isMobile={isMobile} onSelectCashPay={onDeleteInsurance} insuranceSection={insuranceSection} patientId={patientId} />}
          kind="editable-row"
          hideLabel={isMobile}
          divider={isMobile}
        >
          {isMobile ? (
            <Box display="flex" flexDirection="column" gap="2rem">
              <ContentStack title={l10n.accountSettings.insurance.network} value={insuranceData?.companyName} />
              <ContentStack title={l10n.accountSettings.insurance.memberId} value={insuranceData?.subscriberId} />
              {!!insuranceData?.groupNumber && <ContentStack title={l10n.accountSettings.insurance.groupNumber} value={insuranceData.groupNumber} />}
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap="0.5rem">
              <Box display="flex" gap="2rem">
                <Typography className={settingsClasses.semiBold}>
                  {insuranceData?.companyName}
                </Typography>
                <Link
                  display="block"
                  component="button"
                  variant="body1"
                  underline="always"
                  onClick={() => setViewState('viewCard')}
                  className={settingsClasses.link}
                >
                  {l10n.accountSettings.insurance.viewInsuranceCard}
                </Link>
              </Box>
              <Typography variant="body2" component="span">
                {l10n.accountSettings.insurance.memberId}&nbsp;
                <Typography color="textPrimary" variant="inherit" className={settingsClasses.semiBold}>
                  {insuranceData?.subscriberId}
                </Typography>
              </Typography>
              {!!insuranceData?.groupNumber && (
                <Typography variant="body2" component="span">
                  {l10n.accountSettings.insurance.groupNumber}&nbsp;
                  <Typography color="textPrimary" variant="inherit" className={settingsClasses.semiBold}>
                    {insuranceData.groupNumber}
                  </Typography>
                </Typography>
              )}
            </Box>
          )}

        </EditableSectionRow>

        <EditableSectionRow
          kind="mobile-button"
          rowKey="insuranceCard"
          onClick={() => setViewState('viewCard')}
          label={l10n.accountSettings.insurance.viewInsuranceCard}
          divider
        />

        <EditableSectionRow
          label={l10n.accountSettings.insurance.primarySubscriber}
          rowKey="primarySubscriber"
          form={<PrimarySubscriberFormBlock isMobile={isMobile} />}
          kind="editable-row"
          hideLabel={isMobile}
          divider
        >
          {isMobile ? (
            <Box display="flex" flexDirection="column" gap="2rem">
              <ContentStack title={l10n.accountSettings.insurance.primarySubscriber} value={primarySubscriberName} />
              <ContentStack title={l10n.accountSettings.insurance.dateOfBirth} value={insuranceData?.birthDate} />
              <ContentStack title={l10n.accountSettings.address} value={primarySubscriberAddressFormatted?.fullAddress} />
            </Box>
          ) : (
            <>
              <ContentStack value={primarySubscriberName} />
              <Box display="flex" flexDirection="column" gap="0.5rem" mt={1}>
                <Typography variant="body2" component="span">
                  {l10n.accountSettings.insurance.dateOfBirth}&nbsp;
                  <Typography color="textPrimary" variant="inherit" className={settingsClasses.semiBold}>
                    {insuranceData?.birthDate}
                  </Typography>
                </Typography>
                <Typography variant="body2" component="span">
                  {l10n.accountSettings.address}&nbsp;
                  <Typography color="textPrimary" variant="inherit" className={settingsClasses.semiBold}>
                    {primarySubscriberAddressFormatted?.fullAddress}
                  </Typography>
                </Typography>
              </Box>
            </>
          )}
        </EditableSectionRow>

        <EditableSectionRow
          kind="custom"
          rowKey="insuranceCard"
        >
          <Box display="flex" justifyContent="center">
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteOutline />}
              onClick={onDeleteInsurance}
            >
              {l10n.accountSettings.insurance.deleteInsurance}
            </Button>
          </Box>
        </EditableSectionRow>

      </EditableSectionContainer>
    </>
  );
};
